import React, { useEffect, useRef, useState } from 'react';
import { Dialog, FormControl, FormHelperText, Icon, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import DialogBase from './DialogBase';
import { Beoordeling } from '../../../models/plan/beoordeling';
import { InfraPlan } from '../../../models/plan/plan';
import { Portfolio } from '../../../models/portfolio/portfolio';
import { portfolioService } from '../../../services/portfolio.service';

interface DialogPortfolioStartProps {
  open: boolean;
  plan?: InfraPlan;
  portfolioId?: string;
  onClose: () => void;
  onSubmit: (beoordeling: any) => void;
}

const MAX_LENGTH = 2000;
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const DialogPortfolioStart: React.FC<DialogPortfolioStartProps> = ({
  open,
  plan,
  portfolioId,
  onClose,
  onSubmit
}) => {
  const [portfolio, setPortfolio] = useState<any>({} as any);
  // const [portfolios, setPortfolios] = useState<any[]>([]);
  const [charCount, setCharCount] = useState(portfolio?.description?.length || 0);

  const [validationErrors, setValidationErrors] = useState<{ 
    name?: string; 
    description?: string;
  }>({});

  const hasPortfolioData = useRef(false);

  
  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasPortfolioData.current) {
      // getPortfolios();
      hasPortfolioData.current = true; // Mark as fetched
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const fetchPortfolio = async () => {
      if (portfolioId) {
        try {
          const portfolio = await portfolioService.getPortfolio(portfolioId);
          if (portfolio) {
            setPortfolio(portfolio);
            setCharCount(portfolio.description?.length || 0);
            setValidationErrors({});
            return;
          }
        } 
        catch (error) {
          console.error('Fout bij ophalen portfolio', error);
        }
      }

      // Nieuwe openen reset alles
      setPortfolio({} as Portfolio);
      setCharCount(0);
      setValidationErrors({});
    };

    fetchPortfolio();
  }, [open]);
  
  const onChangeDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidationErrors(prev => ({ ...prev, description: undefined }));
    // Create a copy of the state and update the displayName
    const updatedPortfolio = {
      ...portfolio, // Copy the current state
      description: event.target.value, // Update displayName
    };

    setCharCount(event.target.value.length);

    // Update the state to trigger re-render
    setPortfolio(updatedPortfolio);
  };

  const onChangeName = (event: any) => {
    setValidationErrors(prev => ({ ...prev, name: undefined }));
    // Create a copy of the state and update the displayName
    const updatedPortfolio = {
      ...portfolio, // Copy the current state
      name: event.target.value, // Update displayName
    };

    // Update the state to trigger re-render
    setPortfolio(updatedPortfolio);
  }

  const handleSubmit = () => {
    const errors: { name?: string; description?: string } = {};

    if (!portfolio.name) {
      errors.name = 'Naam is verplicht';
    }

    if (portfolio.description && portfolio.description.length > 2000) {
      errors.description = 'Omschrijving mag maximaal 2000 tekens bevatten';
    }

    if (!portfolio.description || portfolio.description.trim() === '') {
      errors.description = 'Omschrijving is verplicht';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    onSubmit(portfolio);
    onClose(); // Close the dialog after submission
  };

  return (
    <DialogBase
      open={open}
      title={`Afstemmingsdossier starten${plan? ` met plan ${plan?.planNaam}` : '' }`}
      contentText="U gaat een afstemmingsdossier aanmaken. Vul hieronder de naam en een eventuele opmerking in. Het plan wordt automatisch toegevoegd aan het dossier."
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText={ portfolioId ? "Opslaan" : "Start met afstemmen" } 
      confirmButtonIcon={ portfolioId ? <Icon>save</Icon> : <Icon>rocket_launch</Icon> }
    >
      <section>
        <label htmlFor="name">Naam</label>
        <br/>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <TextField
            required
            // sx={{ mt: 2 }}
            id="name"
            label="Naam" 
            variant="filled"
            value={portfolio?.name ? String(portfolio.name) : ""}
            onChange={onChangeName}
            error={!!validationErrors.name}
          />
          {validationErrors.name && (
            <span style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>
              {validationErrors.name}
            </span>
          )}
        </FormControl>
      </section>

      <section>
        <label htmlFor="description">Omschrijving</label>
        <br/>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <TextField
            required
            id="description"
            label="Vul een omschrijving in"
            variant="filled"
            multiline
            rows={5}
            // maxRows={10}
            onChange={onChangeDescription}
            value={portfolio?.description ? String(portfolio.description) : ""}
            error={!!validationErrors.description}
            helperText={validationErrors.description}
            inputProps={{ maxLength: MAX_LENGTH }}
          />
          <FormHelperText
            sx={{
              textAlign: 'right',
              fontSize: '0.8rem',
              color: charCount >= MAX_LENGTH ? 'error.main' : 'text.secondary',
              marginRight: 1,
            }}
          >
            {charCount} / {MAX_LENGTH}
          </FormHelperText>
          {/* {validationErrors.remark && (
            <span style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>
              {validationErrors.remark}
            </span>
          )} */}
        </FormControl>
      </section>
      
    </DialogBase>
  );
};

export default DialogPortfolioStart;
